import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  InputAdornment,
  MenuItem,
  Typography,
} from "@mui/material";
import { AutonomousActivitySwitcher } from "portal/components/atk/AutonomousActivitySwitcher";
import {
  AutonomousActivityType,
  ScvAutonomyAuxControlAction,
} from "protos/atk/enums";
import { Field, useFormikContext } from "formik";
import { Select as FormikSelect, TextField } from "formik-mui";
import { friendlyScvInputName } from "portal/utils/atkEquipment";
import { RigDetailsComputedFormState, RigDetailsFormState } from "../FormState";
import { ScvAuxControlSelect } from "portal/components/atk/ScvAuxControlSelect";
import { typedPath } from "typed-path";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useContext, useState } from "react";

const S = typedPath<RigDetailsFormState>();

export const ScvAutonomySubform: React.FC<{ readOnly: boolean }> = ({
  readOnly,
}) => {
  const { t } = useTranslation();
  const { scvConnectionSelectOptions } = useContext(
    RigDetailsComputedFormState
  );
  const { values, setFieldValue, setFieldTouched, validateForm } =
    useFormikContext<RigDetailsFormState>();
  const [viewingHeightActivity, setViewingHeightActivity] =
    useState<AutonomousActivityType>(
      AutonomousActivityType.ACTIVITY_WORKING_ROW
    );
  const [viewingAux1Activity, setViewingAux1Activity] =
    useState<AutonomousActivityType>(
      AutonomousActivityType.ACTIVITY_WORKING_ROW
    );
  const [viewingAux2Activity, setViewingAux2Activity] =
    useState<AutonomousActivityType>(
      AutonomousActivityType.ACTIVITY_WORKING_ROW
    );

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {t(
          "views.autotractor.rigDetails.configuration.scvAutonomy.sectionHeader"
        )}
      </AccordionSummary>
      <AccordionDetails className="grid grid-cols-2 gap-4">
        <FormControl fullWidth className="col-span-full">
          <Typography>
            {t(
              "views.autotractor.rigDetails.configuration.scvAutonomy.heightScv"
            )}
          </Typography>
          <Field
            component={FormikSelect}
            name={S.atkConfig.scvAutonomy.heightScvConnectionId.$path}
            displayEmpty
            disabled={readOnly}
          >
            <MenuItem value="">{t("utils.descriptors.none")}</MenuItem>
            {scvConnectionSelectOptions.map((option) => (
              <MenuItem
                key={option.connectionId}
                value={option.connectionId}
                disabled={!option.positionFeedback}
              >
                <Typography>
                  {friendlyScvInputName(t, option.implement, option.scvInput)}
                  {!option.positionFeedback && (
                    <Typography variant="body2" className="ml-2">
                      {t(
                        "views.autotractor.rigDetails.configuration.scvAutonomy.noPositionSensor"
                      )}
                    </Typography>
                  )}
                </Typography>
              </MenuItem>
            ))}
          </Field>
        </FormControl>
        {values.atkConfig.scvAutonomy.heightScvConnectionId !== "" && (
          <>
            <Typography>
              {t(
                "views.autotractor.autonomousActivities.activitySelectionLabel"
              )}
            </Typography>
            <AutonomousActivitySwitcher
              activeActivity={viewingHeightActivity}
              setActiveActivity={setViewingHeightActivity}
            />
            <FormControl fullWidth>
              <div id="height-control-label">
                <Typography id="height-control-mode-label">
                  {t(
                    "views.autotractor.autonomousActivities.heightControlModeLabel"
                  )}
                </Typography>
              </div>
              <Field
                component={FormikSelect}
                name={
                  S.atkConfig.activities.activities[viewingHeightActivity]
                    .heightActivity.$path
                }
                aria-labelledby="height-control-mode-label"
                disabled={readOnly}
              >
                <MenuItem value="none">
                  {t(
                    "views.autotractor.autonomousActivities.heightControlMode.none"
                  )}
                </MenuItem>
                <MenuItem value="scv-off">
                  {t(
                    "views.autotractor.autonomousActivities.heightControlMode.scvOff"
                  )}
                </MenuItem>
                <MenuItem value="hold-height">
                  {t(
                    "views.autotractor.autonomousActivities.heightControlMode.holdHeight"
                  )}
                </MenuItem>
              </Field>
            </FormControl>
            <FormControl fullWidth>
              <Typography id="target-height-label">
                {t(
                  "views.autotractor.autonomousActivities.heightControlTargetPositionLabel"
                )}
              </Typography>
              <Field
                component={TextField}
                name={
                  S.atkConfig.activities.activities[viewingHeightActivity]
                    .heightPct.$path
                }
                disabled={
                  values.atkConfig.activities.activities[viewingHeightActivity]
                    ?.heightActivity !== "hold-height" || readOnly
                }
                aria-labelledby="target-height-label"
                className="mt-2"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {t("utils.units.%")}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </FormControl>
          </>
        )}
        <hr className="w-full col-span-full" />
        <FormControl fullWidth className="col-span-full">
          <Typography>
            {t(
              "views.autotractor.rigDetails.configuration.scvAutonomy.aux1Scv"
            )}
          </Typography>
          <Field
            component={FormikSelect}
            name={S.atkConfig.scvAutonomy.aux1ScvConnectionId.$path}
            displayEmpty
            disabled={readOnly}
          >
            <MenuItem value="">{t("utils.descriptors.none")}</MenuItem>
            {scvConnectionSelectOptions.map((option) => (
              <MenuItem key={option.connectionId} value={option.connectionId}>
                {friendlyScvInputName(t, option.implement, option.scvInput)}
              </MenuItem>
            ))}
          </Field>
        </FormControl>
        {values.atkConfig.scvAutonomy.aux1ScvConnectionId !== "" && (
          <>
            <Typography>
              {t(
                "views.autotractor.autonomousActivities.activitySelectionLabel"
              )}
            </Typography>
            <AutonomousActivitySwitcher
              activeActivity={viewingAux1Activity}
              setActiveActivity={setViewingAux1Activity}
            />
            <FormControl fullWidth className="col-span-full mt-2">
              <Typography variant="body1" id="aux1-label">
                {t(
                  "views.autotractor.autonomousActivities.aux1EntryActionLabel"
                )}
              </Typography>
              <ScvAuxControlSelect
                aria-labelledby="aux1-label"
                value={
                  values.atkConfig.activities.activities[viewingAux1Activity]
                    ?.aux1Action ??
                  ScvAutonomyAuxControlAction.AUX_CONTROL_ACTION_NONE
                }
                onChange={(e) => {
                  const name =
                    S.atkConfig.activities.activities[viewingAux1Activity]
                      .aux1Action.$path;

                  // work around https://github.com/jaredpalmer/formik/issues/1191
                  // and https://github.com/jaredpalmer/formik/issues/2083
                  setFieldValue(name, e.target.value).finally(() =>
                    setFieldTouched(name, true)
                  );
                  validateForm();
                }}
                disabled={readOnly}
              />
            </FormControl>
          </>
        )}
        <hr className="w-full col-span-full" />
        <FormControl fullWidth className="col-span-full">
          <Typography>
            {t(
              "views.autotractor.rigDetails.configuration.scvAutonomy.aux2Scv"
            )}
          </Typography>
          <Field
            component={FormikSelect}
            name={S.atkConfig.scvAutonomy.aux2ScvConnectionId.$path}
            displayEmpty
            disabled={readOnly}
          >
            <MenuItem value="">{t("utils.descriptors.none")}</MenuItem>
            {scvConnectionSelectOptions.map((option) => (
              <MenuItem key={option.connectionId} value={option.connectionId}>
                {friendlyScvInputName(t, option.implement, option.scvInput)}
              </MenuItem>
            ))}
          </Field>
        </FormControl>
        {values.atkConfig.scvAutonomy.aux2ScvConnectionId !== "" && (
          <>
            <Typography>
              {t(
                "views.autotractor.autonomousActivities.activitySelectionLabel"
              )}
            </Typography>
            <AutonomousActivitySwitcher
              activeActivity={viewingAux2Activity}
              setActiveActivity={setViewingAux2Activity}
            />
            <FormControl fullWidth className="col-span-full mt-2">
              <Typography variant="body1" id="aux2-label">
                {t(
                  "views.autotractor.autonomousActivities.aux2EntryActionLabel"
                )}
              </Typography>
              <ScvAuxControlSelect
                aria-labelledby="aux2-label"
                value={
                  values.atkConfig.activities.activities[viewingAux2Activity]
                    ?.aux2Action ??
                  ScvAutonomyAuxControlAction.AUX_CONTROL_ACTION_NONE
                }
                onChange={(e) => {
                  const name =
                    S.atkConfig.activities.activities[viewingAux2Activity]
                      .aux2Action.$path;

                  // work around https://github.com/jaredpalmer/formik/issues/1191
                  // and https://github.com/jaredpalmer/formik/issues/2083
                  setFieldValue(name, e.target.value).finally(() =>
                    setFieldTouched(name, true)
                  );
                  validateForm();
                }}
                disabled={readOnly}
              />
            </FormControl>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
